import React, { useState, useEffect } from "react";
import cancel from "../assets/images/navbar/cancel.png";
import RestaurantsCard from "../components/RestaurantsCard";
import { restaurantService } from "../services/index";
import { RestaurantI } from "../utils/types";
import defaultImage from "../assets/images/cuisines/default.jpeg";

interface RestaurantsByCuisineModalProps {
  isOpen: boolean;
  onClose: () => void;
  cuisineId: string;
  cuisineName: string;
}

const RestaurantsByCuisineModal: React.FC<RestaurantsByCuisineModalProps> = ({
  isOpen,
  onClose,
  cuisineId,
  cuisineName,
}) => {
  const [restaurants, setRestaurants] = useState<RestaurantI[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState<number>(1);
  const [hasMore, setHasMore] = useState<boolean>(true);

  useEffect(() => {
    if (!isOpen || !cuisineId) return;

    const fetchRestaurants = async () => {
      try {
        setLoading(true);
        const response: any = await restaurantService.getRestaurantsByGroup(
          "category",
          cuisineId,
          {
            limit: 20,
            page: 1,
          }
        );

        // Handle response based on API structure
        const data = response.data || response;
        console.log("data", data)
        setRestaurants(Array.isArray(data) ? data : []);
        
        // Check if there are more pages
        if (response.pagination) {
          setHasMore(response.pagination.currentPage < response.pagination.totalPages);
        }
        
        setError(null);
        setPage(1);
      } catch (err: any) {
        console.error("Error fetching restaurants:", err);
        setError(err.message || "Failed to load restaurants");
      } finally {
        setLoading(false);
      }
    };

    fetchRestaurants();
  }, [isOpen, cuisineId]);

  const loadMore = async () => {
    if (!hasMore || loading) return;

    try {
      setLoading(true);
      const nextPage = page + 1;
      const response: any = await restaurantService.getRestaurantsByGroup(
        "cuisinetype",
        cuisineId,
        {
          limit: 20,
          page: nextPage,
        }
      );

      const data = response.data || response;
      setRestaurants((prev) => [...prev, ...(Array.isArray(data) ? data : [])]);
      
      if (response.pagination) {
        setHasMore(response.pagination.currentPage < response.pagination.totalPages);
      }
      
      setPage(nextPage);
    } catch (err: any) {
      console.error("Error loading more restaurants:", err);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop Overlay */}
      <div
        className="hidden md:block fixed inset-0 bg-black bg-opacity-50 dark:bg-opacity-70 z-[1005] transition-colors duration-300"
        onClick={onClose}
      />

      {/* Modal Container */}
      <div className="fixed inset-0 md:flex md:items-center md:justify-center z-[1006] md:pointer-events-none">
        <div
          className="h-full w-full md:h-auto md:max-w-[95vw] md:max-h-[85vh] bg-white dark:bg-gray-800 md:rounded-[24px] md:pointer-events-auto relative transition-colors duration-300 overflow-hidden flex flex-col"
          style={{
            boxShadow: "-23px 23px 72px 0 rgba(0, 0, 0, 0.15)",
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header with Back/Close Button */}
          <div className="flex items-center justify-between p-4 md:p-6 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
            <h2 className="text-gray-900 dark:text-white font-urbanist text-xl md:text-2xl font-semibold">
              {cuisineName} Restaurants
            </h2>

            {/* Close Button */}
            <div
              className="h-[36px] w-[36px] cursor-pointer border dark:border-gray-600 rounded-full p-[7px] bg-white dark:bg-gray-700 flex items-center justify-center hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
              onClick={onClose}
            >
              <img src={cancel} alt="Close" className="h-[18px] w-[18px]" />
            </div>
          </div>

          {/* Content Area */}
          <div className="flex-1 overflow-y-auto p-4 md:p-6">
            {loading && restaurants.length === 0 ? (
              <div className="flex justify-center items-center py-8">
                <p className="text-gray-600 dark:text-gray-400">
                  Loading restaurants...
                </p>
              </div>
            ) : error ? (
              <div className="flex justify-center items-center py-8">
                <p className="text-red-600 dark:text-red-400">Error: {error}</p>
              </div>
            ) : restaurants.length === 0 ? (
              <div className="flex justify-center items-center py-8">
                <p className="text-gray-600 dark:text-gray-400">
                  No restaurants found for this cuisine
                </p>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                  {restaurants.map((restaurant) => (
                    <RestaurantsCard
                      key={restaurant._id}
                      _id={restaurant._id}
                      name={restaurant.businessName}
                      image={
                        restaurant.kitchenPhotos?.[0] ||
                        defaultImage
                      }
                      status={restaurant.deliveryAvailable ? "open" : "closed"}
                      star={4.5}
                      reviews={100}
                      time={`${restaurant.avgPreparationTime || 30} min`}
                      location={restaurant.location?.city || ""}
                    />
                  ))}
                </div>

                {/* Load More Button */}
                {hasMore && (
                  <div className="flex justify-center mt-6">
                    <button
                      onClick={loadMore}
                      disabled={loading}
                      className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      {loading ? "Loading..." : "Load More"}
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default RestaurantsByCuisineModal;
